import * as React from "react";
import Timeline from "@mui/lab/Timeline";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import Typography from "@mui/material/Typography";
import { invokeApi } from "../../../utils/http-service";
import { HTTP_METHODS } from "../../../utils/http-service";
import {
  HOSTNAME,
  INVOICE_TRACKING_HOSTNAME
} from "../../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../../common-utilities/utils/end-points/invoice-tracking";
import { toast } from "react-toastify";
import { Box, Button, IconButton, Link, Stack, TextareaAutosize } from "@mui/material";
import { convertToCamelCaseFromUnderScore } from "../../../../common-utilities/utils";
import { AccessTime, BlockOutlined, CancelOutlined, CheckCircle } from "@mui/icons-material";
import CustomModal from "../../../../common-utilities/core/modal";
import FileUpload from "../../../../common-utilities/core/file-uploader";
import { TimelineOppositeContent } from "@mui/lab";
import ApprovalDetailsCard from "./approval-details-card";
import { getOfflineData } from "../../../utils/offline-services";


const getColor = (finalStageStatus) => {
  if (finalStageStatus === "PENDING") {
    return "grey";
  } else if (finalStageStatus === "APPROVED") {
    return "primary";
  } else if (finalStageStatus === "REJECTED") {
    return "error";
  }

  return "grey";
};

const getIcon = (finalStageStatus) => {
  if (finalStageStatus === "PENDING") {
    return <AccessTime />;
  } else if (finalStageStatus === "APPROVED") {
    return <CheckCircle />;
  } else if (finalStageStatus === "REJECTED") {
    return <BlockOutlined />;
  }
  return <AccessTime />;
};

export default function CustomizedTimeline({
  documentDetails,
  onSuccess,
  docId
}) {
  const [approveRejectDetails, setApproveRejectDetails] = React.useState(null);
  const { stages = [], stagePassed } = documentDetails;
  const userDetails = getOfflineData("user") || {};

  const onApprove = () => {
    const payload = {
      status: approveRejectDetails.type === 'Approve' ? "APPROVED" : "REJECTED",
      comment: approveRejectDetails.comment
    };
    if (stages[stagePassed].docUpload) {
      payload.uploadedDoc = approveRejectDetails.uploadedDoc || "legal2upload";
    }

    invokeApi(
      HTTP_METHODS.PUT,
      `${INVOICE_TRACKING_HOSTNAME}${REST_URLS.LG_APPROVAL}${docId}`,
      payload
    )
      .then((response) => {
        if (response.message) {
          toast.error(response.message || "");
          setApproveRejectDetails(null);
        } else {
          setApproveRejectDetails(null);
          onSuccess();
        }
      })
      .catch((err) => {
        toast.error("Failed to update");
      });
  };

  const isDisabled = () => {
    const { comment, uploadedDoc } = approveRejectDetails;
    if (stages[stagePassed].docUpload) {
      return !comment || !uploadedDoc;
    }
    return !comment;
  };

  const checkShowApprovalButton = (step) => {
    if (stages.some((stage) => stage.finalStageStatus === "REJECTED")) {
      return false
    }
    return step?.stageNumber === stagePassed + 1;

  }

  return (
    <Box>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0
          }
        }}
      >
        {stages.map((step, index) => {
          return (
            <TimelineItem key={step.stageName}>
              <TimelineOppositeContent color='text.secondary'>
                <Typography variant='h6' component='span'>
                  {convertToCamelCaseFromUnderScore(step.stageName)}
                </Typography>
                <Typography>Approval Type : {step.approvalType}</Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineConnector />
                <TimelineDot color={getColor(step.finalStageStatus)}>
                  {getIcon(step.finalStageStatus)}
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent sx={{ py: "12px", px: 2 }}>
                {step?.approvers.map((approver) => {
                  return <ApprovalDetailsCard role={userDetails?.role} approver={approver} onClick={(type) => setApproveRejectDetails({ type })} showButton={checkShowApprovalButton(step)} />;
                })}
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      {approveRejectDetails && (
        <CustomModal
          title={
            approveRejectDetails.type === 'Approve'
              ? "Approve Document"
              : "Reject Document"
          }
          disabledOutsideClick={true}
          onClose={() => {
            setApproveRejectDetails(null);
          }}
        >
          <div className='b-modal-form-container'>
            <TextareaAutosize
              className='new-textfield'
              minRows={6}
              placeholder="Comments *"
              style={{ width: "100%" }}
              value={approveRejectDetails.comment}
              onChange={(e) => {
                setApproveRejectDetails({
                  ...approveRejectDetails,
                  comment: e.target.value
                });
              }}
            />
            {approveRejectDetails.uploadedDoc ? (
              <Stack direction={"row"} alignItems="center" spacing={2}>
                <Link href={approveRejectDetails.uploadedDoc} target="_blank">
                  Document
                </Link>
                <IconButton
                  onClick={() => {
                    setApproveRejectDetails({
                      ...approveRejectDetails,
                      uploadedDoc: "",
                    });
                  }}
                >
                  <CancelOutlined color="red" />
                </IconButton>
              </Stack>
            ) : (
              <FileUpload
                id="docupload"
                buttonBackground="#fff"
                buttonColor="#008938"
                mainButtonClass="subway-finance-bulk-upload"
                borderColor="rgba(0, 138, 82, 0.5)"
                uploadText= {stages[stagePassed]?.docUpload ? "documents *" : "documents"}
                clsName="finance-master-data-uploader"
                url={`${HOSTNAME}${REST_URLS.UPLOAD_DOCUMENT}`}
                fullWidth
                callBack={(response) => {
                  const { data } = response;
                  setApproveRejectDetails({
                    ...approveRejectDetails,
                    uploadedDoc: data.url,
                  });
                }}
              />
            )}

            <Button
              color={`${approveRejectDetails.type === 'Approve' ? "primary" : "error"}`}
              variant='outlined'
              onClick={onApprove}
              disabled={isDisabled()}
              fullWidth
            >
              {approveRejectDetails.type === 'Approve' ? "Approve" : "Reject"}
            </Button>
          </div>
        </CustomModal>
      )}
    </Box>
  );
}
